import { ParcelLabelsDialogBody } from "@/components/parcel-labels-dialog-body";
import { MIN_PARCELS, useLabelPrinting } from "@/hooks/use-label-printing";
import { printInvoiceDeliverySticker, printInvoiceDeliveryStickerZpl } from "@/lib/invoices-payments";

interface Props {
  invoiceId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Prints one 100x150mm parcel label per parcel ("1 of 5", "2 of 5", …) for an invoice.
 *
 * The job screen's dialog minus the seeded count: an invoice carries no dispatch record, so the
 * box starts at one parcel and the operator says how many. Everything else — the agent probe, the
 * printer picker, the dispatch branch and the ZPL diagnostic — is `useLabelPrinting` and
 * `<ParcelLabelsDialogBody>`, shared with the job dialog so the two entry points cannot drift on
 * where the labels go, nor on what the dialog says about it.
 *
 * The entry point exists because many deliveries are invoiced without ever becoming a job; the
 * server resolves the delivery party from the linked job when there is one and from the invoice's
 * own client when there is not.
 */
export function PrintInvoiceLabelsDialog({ invoiceId, open, onOpenChange }: Props) {
  const labels = useLabelPrinting({
    open,
    onOpenChange,
    initialCount: MIN_PARCELS,
    fetchPdf: (parcelCount) => printInvoiceDeliverySticker(invoiceId, parcelCount),
    // No disposition, unlike the job route: this endpoint records nothing, so there is no
    // event feed for a diagnostic download to pollute and nothing to distinguish.
    fetchZpl: (parcelCount) => printInvoiceDeliveryStickerZpl(invoiceId, parcelCount),
    downloadFileName: `parcel-labels-invoice-${invoiceId}.zpl`,
  });

  return <ParcelLabelsDialogBody open={open} onOpenChange={onOpenChange} labels={labels} />;
}
